import { inject, Injectable } from '@angular/core';
import { Platform } from '@ionic/angular/standalone';
import OneSignal, { NotificationClickEvent, NotificationWillDisplayEvent } from 'onesignal-cordova-plugin';
import { defer, forkJoin, Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, first, switchMap, tap } from 'rxjs/operators';
import { Auth } from 'src/app/core/common/auth';
import { ICoreUser } from 'src/app/core/contracts';
import { log, Logger } from 'src/app/core/helpers/logger';
import { DataPrivacyData } from 'src/app/data/data-privacy.data';
import { UserData } from 'src/app/data/user.data';
import { environment } from 'src/environments/environment';

@Injectable()
export class Push {
  private auth = inject(Auth);
  private platform = inject(Platform);
  private logger = inject(Logger);
  private userData = inject(UserData);
  private dataPrivacy = inject(DataPrivacyData);
  private phone: string;
  private email: string;

  public init(
    onPushOpened: (ev: NotificationClickEvent) => void = () => {},
    onPushReceived: (ev: NotificationWillDisplayEvent) => void = (event: NotificationWillDisplayEvent) => {
      event.getNotification().display();
    }
  ) {
    if (!this.platform.is('capacitor')) {
      // add browser push configs
      return;
    }

    OneSignal.initialize(environment.oneSignalAppId);

    // Marketing in-app messages must not render during startup — they previously caused
    // startup crashes, and they would draw behind the ATT / push permission system alerts.
    // Resumed by the initializer via resumeInAppMessages() once the permission flow
    // completes and the app has settled.
    OneSignal.InAppMessages.setPaused(true);

    OneSignal.Notifications.addEventListener('click', onPushOpened);
    OneSignal.Notifications.addEventListener('foregroundWillDisplay', onPushReceived);
    OneSignal.Location.setShared(true);

    // Keep the OneSignal user in lock-step with the app's auth state. The per-state side effects run *inside*
    // the stream (via switchMap, not the subscribe callback) so that when auth flips, switchMap unsubscribes
    // the previous branch — including any in-flight email lookup — before starting the next. That cancellation
    // is what stops a late addEmail from racing past sign-out and tagging a signed-out user.
    this.auth.isAuthenticated$
      .pipe(
        distinctUntilChanged(), // isAuthenticated$ re-emits on silent token renew — ignore no-op repeats
        switchMap(isAuthenticated =>
          isAuthenticated
            ? this.auth.registeredUser$.pipe(
                // auth.user$ may not be ready the moment isAuthenticated flips true — wait for the first real user
                filter((user): user is ICoreUser => !!user),
                first()
              )
            : of(null)
        ),
        switchMap(user => (user?.sub ? this.attachUser(user) : this.detachUser()))
      )
      .subscribe();
  }

  // Attaches the signed-in customer's identity to OneSignal (external id + SMS, then email) so marketing
  // campaigns can target them. Does NOT opt the push subscription in — that follows notification permission
  // (see requestPermission()), not auth. Returned into the auth stream's switchMap: if the customer
  // signs out before the email lookup resolves, switchMap unsubscribes it and addEmail never fires.
  private attachUser(user: ICoreUser): Observable<unknown> {
    return defer(() => {
      OneSignal.login(user.sub);

      this.phone = this.normalizePhone(user.preferred_username);
      if (this.phone) {
        OneSignal.User.addSms(this.phone);
      }

      return this.linkEmail();
    }).pipe(
      catchError(err => {
        this.logger.error('@push: failed to attach OneSignal user', { err });
        return of(null);
      })
    );
  }

  // Detaches the customer from OneSignal on sign-out: removes the SMS/email subscriptions we added and
  // disassociates the identity. It deliberately does NOT opt the push subscription out — opt-in follows
  // notification permission (see requestPermission()), not auth, so a permitted device keeps receiving
  // broadcast push after logout. Re-adding optOut here would resurrect the startup opt-in/opt-out race.
  private detachUser(): Observable<unknown> {
    return defer(() => {
      if (this.phone) {
        OneSignal.User.removeSms(this.phone);
        this.phone = null;
      }
      if (this.email) {
        OneSignal.User.removeEmail(this.email);
        this.email = null;
      }
      OneSignal.logout();
      return of(null);
    }).pipe(
      catchError(err => {
        this.logger.error('@push: failed to detach OneSignal user', { err });
        return of(null);
      })
    );
  }

  // Links the registered customer's email to their OneSignal user so they can be targeted by email
  // marketing campaigns. Signing up in the app implies marketing-email consent, but older users must
  // first accept it via the data-privacy popover — so addEmail is gated on the customer's
  // marketing-consent status (consentReceivingOffers) to cover both cases.
  private linkEmail(): Observable<unknown> {
    // Silent background sync — no global spinner, no error toast; failures are swallowed below.
    const options = { handleLoading: false, handleError: false };
    return forkJoin({
      customer: this.userData.getCustomer(options),
      consent: this.dataPrivacy.getDataConsent(options),
    }).pipe(
      tap(({ customer, consent }) => {
        const email = customer?.email?.trim();
        if (email && consent?.consentReceivingOffers) {
          this.email = email;
          OneSignal.User.addEmail(email);
        }
      }),
      catchError(() => of(null))
    );
  }

  private normalizePhone(username: string | number): string {
    const phone = username?.toString();
    if (!phone) {
      return null;
    }
    return phone.startsWith('+') ? phone : `+${phone}`;
  }

  /**
   * Shows the push-notification permission system prompt (on later launches iOS/Android resolve
   * instantly with the stored answer, no alert). Called by the initializer after the ATT prompt
   * is answered so the two system alerts never race each other.
   *
   * Notification permission is the single source of truth for the push opt-in state: a permitted
   * device stays subscribed across login/logout (so guests still receive broadcasts). The auth
   * stream (attachUser/detachUser) never touches opt-in/opt-out, so this is the only opt-in
   * writer and there is no second writer to race at startup.
   */
  public async requestPermission() {
    if (!this.platform.is('capacitor')) {
      return;
    }
    try {
      const accepted = await OneSignal.Notifications.requestPermission(true);
      if (accepted) {
        log('@push: user accepted notifications permission');
        OneSignal.User.pushSubscription.optIn();
      } else {
        this.logger.warn('@push: user refused notifications permission');
      }
    } catch (err) {
      // must not break the caller's startup chain (in-app messages resume depends on it)
      this.logger.error('@push: requestPermission failed', { err });
    }
  }

  /** Re-allows OneSignal in-app messages after startup — see the pause in init(). */
  public resumeInAppMessages() {
    if (this.platform.is('capacitor')) {
      OneSignal.InAppMessages.setPaused(false);
    }
  }
}
